// @ts-nocheck
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faMicrophone, faPaperclip } from '@fortawesome/free-solid-svg-icons';

interface BeautifulInputFieldProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder?: string;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  sendButtonColor?: string;
  inputBgColor?: string;
  inputTextColor?: string;
  borderColor?: string;
  showVoiceInput?: boolean;
  showAttachment?: boolean;
  onVoiceClick?: () => void;
  onAttachmentClick?: () => void;
  maxRows?: number;
}

/**
 * Beautiful Input Field Component
 * Modern, smooth design matching the reference image
 */
const BeautifulInputField = forwardRef<HTMLTextAreaElement, BeautifulInputFieldProps>(({
  value,
  onChange,
  onSend,
  placeholder = 'Ask me anything...',
  disabled = false,
  onKeyDown,
  sendButtonColor = '#667eea',
  inputBgColor = '#ffffff',
  inputTextColor = '#111827',
  borderColor = '#e5e7eb',
  showVoiceInput = false,
  showAttachment = false,
  onVoiceClick,
  onAttachmentClick,
  maxRows = 4
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Expose focus method to parent via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    }
  } as any));

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const lineHeight = 20; // approximate
      const maxHeight = lineHeight * maxRows;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [value, maxRows]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) {
        onSend();
      }
    }
    onKeyDown?.(e);
  };

  const handleSendClick = () => {
    if (value.trim() && !disabled) {
      onSend();
    }
  };

  return (
    <div
      className="beautiful-input-container"
      style={{
        background: inputBgColor,
        borderColor: isFocused ? sendButtonColor : borderColor,
        boxShadow: isFocused
          ? `0 4px 12px ${sendButtonColor}20`
          : '0 2px 8px rgba(0, 0, 0, 0.04)',
        border: `1.5px solid`,
        borderRadius: '24px',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        transition: 'all 0.2s ease',
        position: 'relative'
      }}
    >
      {/* Optional attachment button */}
      {showAttachment && (
        <button
          onClick={onAttachmentClick}
          disabled={disabled}
          className="input-action-btn"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: disabled ? 'not-allowed' : 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s ease',
            opacity: disabled ? 0.5 : 1
          }}
          onMouseOver={(e) => !disabled && (e.currentTarget.style.color = sendButtonColor)}
          onMouseOut={(e) => (e.currentTarget.style.color = '#9ca3af')}
          title="Attach file"
        >
          <FontAwesomeIcon icon={faPaperclip} size="sm" />
        </button>
      )}

      {/* Text input */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
        className="beautiful-input"
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          fontSize: '14px',
          color: inputTextColor,
          background: 'transparent',
          resize: 'none',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          lineHeight: '20px',
          maxHeight: `${maxRows * 20}px`,
          overflowY: 'auto',
          padding: '8px 0'
        }}
      />

      {/* Optional voice input button */}
      {showVoiceInput && !value.trim() && (
        <button
          onClick={onVoiceClick}
          disabled={disabled}
          className="input-action-btn"
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            cursor: disabled ? 'not-allowed' : 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s ease',
            opacity: disabled ? 0.5 : 1
          }}
          onMouseOver={(e) => !disabled && (e.currentTarget.style.color = sendButtonColor)}
          onMouseOut={(e) => (e.currentTarget.style.color = '#9ca3af')}
          title="Voice input"
        >
          <FontAwesomeIcon icon={faMicrophone} size="sm" />
        </button>
      )}

      {/* Send button */}
      <button
        onClick={handleSendClick}
        disabled={!value.trim() || disabled}
        className="beautiful-send-button"
        style={{
          width: '36px',
          height: '36px',
          minWidth: '36px',
          minHeight: '36px',
          borderRadius: '50%',
          // Keep the brand color even when disabled so the button stays
          // visible against the input background (#f9fafb / #ffffff). We
          // lower opacity to communicate the disabled state instead of
          // washing the background to near-white.
          background: value.trim() && !disabled
            ? `linear-gradient(135deg, ${sendButtonColor} 0%, ${sendButtonColor}dd 100%)`
            : sendButtonColor,
          color: 'white',
          border: 'none',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: value.trim() && !disabled ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s ease',
          boxShadow: value.trim() && !disabled
            ? `0 4px 12px ${sendButtonColor}40`
            : 'none',
          transform: value.trim() && !disabled ? 'scale(1)' : 'scale(0.95)',
          opacity: value.trim() && !disabled ? 1 : 0.55
        }}
        onMouseOver={(e) => {
          if (value.trim() && !disabled) {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = `0 6px 16px ${sendButtonColor}50`;
          }
        }}
        onMouseOut={(e) => {
          if (value.trim() && !disabled) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = `0 4px 12px ${sendButtonColor}40`;
          }
        }}
        onMouseDown={(e) => {
          if (value.trim() && !disabled) {
            e.currentTarget.style.transform = 'scale(0.95)';
          }
        }}
        onMouseUp={(e) => {
          if (value.trim() && !disabled) {
            e.currentTarget.style.transform = 'scale(1.05)';
          }
        }}
        title="Send message"
      >
        <FontAwesomeIcon
          icon={faPaperPlane}
          size="sm"
          style={{
            display: 'block',
            margin: 'auto'
          }}
        />
      </button>

      <style>{`
        .beautiful-input::-webkit-scrollbar {
          width: 4px;
        }
        .beautiful-input::-webkit-scrollbar-track {
          background: transparent;
        }
        .beautiful-input::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 2px;
        }
        .beautiful-input::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .beautiful-input::placeholder {
          color: #9ca3af;
        }
        /* Force remove outline on focus */
        .beautiful-input:focus,
        .beautiful-input:focus-visible {
          outline: none !important;
          border: none !important;
          box-shadow: none !important;
        }
        textarea.beautiful-input:focus,
        textarea.beautiful-input:focus-visible {
          outline: none !important;
          border: none !important;
          box-shadow: none !important;
        }
        /* Force send button to remain visible even when disabled.
           Some host sites ship global rules like
           "button:disabled { display: none }" or "visibility: hidden"
           which would otherwise hide our send icon on first load /
           during streaming. Override them with !important here so the
           inline styles (which set color/opacity) still take effect. */
        .beautiful-send-button,
        .beautiful-send-button:disabled,
        .beautiful-send-button[disabled],
        button.beautiful-send-button,
        button.beautiful-send-button:disabled,
        button.beautiful-send-button[disabled] {
          display: flex !important;
          visibility: visible !important;
          align-items: center !important;
          justify-content: center !important;
          pointer-events: auto !important;
        }
        .beautiful-send-button:disabled,
        .beautiful-send-button[disabled] {
          pointer-events: none !important;
        }
        .beautiful-send-button svg,
        .beautiful-send-button:disabled svg,
        .beautiful-send-button[disabled] svg {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        .input-action-btn,
        .input-action-btn:disabled,
        .input-action-btn[disabled] {
          display: flex !important;
          visibility: visible !important;
        }
      `}</style>
    </div>
  );
});

BeautifulInputField.displayName = 'BeautifulInputField';

export default BeautifulInputField;
